/**
 * Default BFF resources object.
 * @module Resources
 */

import { keyMirror } from 'core/utilities';

import * as presenters from '../presenters';
import { CartData } from './endpoints';
import { HTTP_METHOD } from './http-method.enum';

export const resources = {
  resellerIntention: {
    endpoint: 'reseller/resale-intention',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.resaleIntention,
    presenterRequest: null,
  },
  authDetail: {
    endpoint: 'auth/detail',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.authDetailPresenter,
    presenterRequest: null,
  },
  cartGet: {
    endpoint: ({
      cycle,
      isExtendedCapitation,
    }: {
      cycle: string | undefined;
      isExtendedCapitation: boolean;
    }) =>
      cycle && isExtendedCapitation
        ? `cart/v2?cycle=${cycle}&isHybridCart=true`
        : `cart/v2?isHybridCart=true`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: null,
  },
  hybridLastCartGet: {
    endpoint: () => 'cart/v2/lastcart?isHybridCart=true',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: null,
  },
  cartItemUpdate: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/item?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: presenters.request.cartItemUpdatePresenter,
  },
  catalogGet: {
    endpoint: ({
      cycle,
      isExtendedCapitation,
    }: Pick<CartData, 'cycle' | 'isExtendedCapitation'>) =>
      cycle && isExtendedCapitation ? `product?cycle=${cycle}` : 'product?',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.catalogPresenter,
    presenterRequest: presenters.request.catalogPresenter,
  },
  productDetailGet: {
    endpoint: `product?availability=available-first`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.productDetail,
    presenterRequest: presenters.request.productDetailPresenter,
  },
  cartSetCoupon: {
    endpoint: ({ orderCode, couponNumber }: Pick<CartData, 'orderCode' | 'couponNumber'>) =>
      // eslint-disable-next-line prettier/prettier
      `cart/v2/${orderCode}/setDiscount?isHybridCart=true&${
        couponNumber ? `couponNumber=${couponNumber}` : ''
      }`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    // eslint-disable-next-line @typescript-eslint/no-empty-function, prettier/prettier
    presenterRequest: () => {},
  },
  cartSetShippingModel: {
    endpoint: () => `cart/v2?isHybridCart=true`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: null,
  },
  campaignGet: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) => `cart/v2/${orderCode}/pointsPrograms`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.campaignPresenter,
    presenterRequest: null,
  },
  campaignItemUpdate: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) => `cart/v2/${orderCode}/pointsPrograms`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: presenters.request.cartItemUpdatePresenter,
  },
  comboGet: {
    endpoint: ({ comboCode, orderCode }: Pick<CartData, 'orderCode' | 'comboCode'>) =>
      `cart/v2/${orderCode}/getCombo/${comboCode}`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.comboPresenter,
    presenterRequest: null,
  },
  comboPost: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/item?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: presenters.request.comboPostPresenter,
  },
  paymentMethodsGet: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/paymentplans?isHybridCart=true&presenter=paymentplans-v3`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.paymentMethodsPresenter,
    presenterRequest: null,
  },
  paymentDetailsSet: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/paymentplan?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: presenters.request.paymentDetailsPresenter,
  },
  paymentDetailsSetNewFlow: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v3/${orderCode}/paymentplan?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: presenters.request.paymentDetailsPresenter,
  },
  orderCreate: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/placeorder?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: null,
  },
  orderCreateNewFlow: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v3/${orderCode}/placeorder?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.cartPresenter,
    presenterRequest: null,
  },
  orderGet: {
    endpoint: ({ orderCode }: { orderCode: string }) => `reseller/order/${orderCode}/detail`,
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.orderPresenter,
    presenterRequest: null,
  },
  searchGet: {
    endpoint: ({
      cycle,
      isExtendedCapitation,
    }: Pick<CartData, 'cycle' | 'isExtendedCapitation'>) =>
      cycle && isExtendedCapitation
        ? `product?availability=available-first&cycle=${cycle}`
        : 'product?availability=available-first',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.catalogPresenter,
    presenterRequest: presenters.request.searchPresenter,
  },
  userCreditGet: {
    endpoint: 'reseller/list/credit',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.creditPresenter,
    presenterRequest: null,
  },
  userBuyIntention: {
    endpoint: ({ code, cycle }: Pick<CartData, 'cycle' | 'code'>) => {
      const searchParams = new URLSearchParams();
      if (code) {
        searchParams.set('distributionCenterCode', code);
      }
      if (cycle) {
        searchParams.set('cycle', cycle);
      }
      const searchString = searchParams.toString();
      const search = searchString.length ? `?${searchString}` : '';

      return `reseller/buyintention${search}`;
    },
    method: HTTP_METHOD.POST,
    presenterResponse: null,
    presenterRequest: presenters.request.buyIntentionPresenter,
  },
  promotionFetch: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/promotion?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.rewardPresenter,
    presenterRequest: null,
  },
  processGet: {
    endpoint: ({ orderCode }: Pick<CartData, 'orderCode'>) =>
      `cart/v2/${orderCode}/process?isHybridCart=true`,
    method: HTTP_METHOD.POST,
    presenterResponse: presenters.response.rewardPresenter,
    presenterRequest: null,
  },
  cyclesGet: {
    endpoint: 'reseller/cycles?presenter=stargate',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.cyclesPresenter,
    presenterRequest: null,
  },
  availableShowcasesGet: {
    endpoint: 'product/showcases',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.showcasesAvailablePresenter,
    presenterRequest: null,
  },
  starlinkPageGet: {
    endpoint: 'starlink/pages',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.starlinkResponsePresenter,
    presenterRequest: null,
  },
  starlinkContentGet: {
    endpoint: 'starlink/content',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.starlinkResponsePresenter,
    presenterRequest: null,
  },
  availableDeliveryModes: {
    endpoint: 'reseller/availabledeliverymodes',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.availableDeliveriesPresenter,
    presenterRequest: null,
  },
  currentDeliveryMode: {
    endpoint: 'reseller/currentdeliverymode?isHybridCart=true',
    method: HTTP_METHOD.POST,
    presenterResponse: null,
    presenterRequest: null,
  },
  changeCyclePost: {
    endpoint: 'reseller/cycle',
    method: HTTP_METHOD.POST,
    presenterResponse: null,
    presenterRequest: null,
  },
  getResellerAvailableCatalogs: {
    endpoint: 'reseller/catalog/share?all=true&presenter=stargate',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.resellerAvailablesCatalogs,
    presenterRequest: null,
  },
  incentiveCampaignsListGet: {
    endpoint: 'loyalty/incentive-campaigns',
    method: HTTP_METHOD.GET,
    presenterResponse: presenters.response.incentiveCampaignsList,
    presenterRequest: null,
  },
} as const;

export type ResourceKeys = keyof typeof resources;
export type ResourceBff = (typeof resources)[ResourceKeys];

export const endpoints = keyMirror(resources);
